import React from 'react';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';

const PrivacyPolicy = () => {
  const EFFECTIVE_DATE = 'August 16, 2026';
  const LAST_UPDATED = 'October 13, 2026';
  const COMPANY_EMAIL = 'info@dopove.com';
  const DPO_EMAIL = 'privacy@dopove.com';
  const COMPANY_ADDRESS = 'Chennai, Tamil Nadu, India';

  return (
    <>
      <SEO title="Privacy Policy" description="How Dopove collects, uses and protects personal data on dopove.com, including live chat, the contact form and the GoingMerry waitlist." canonical="https://dopove.com/privacy-policy" />
    <div className="bg-[#191d2b] min-h-screen text-[#fefdfe] flex flex-col font-sans selection:bg-[#e1dabe]/30">
      <main className="flex-1 pt-20">
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">Privacy Policy</h1>
            <p className="text-[#e1dabe] font-mono uppercase tracking-widest text-sm mb-4">
              Effective Date: {EFFECTIVE_DATE} | Last Updated: {LAST_UPDATED}
            </p>
            <p className="text-[#fefdfe]/60 text-sm mb-12">
              <strong>Your privacy matters.</strong> This Policy explains how we collect, use, disclose, and safeguard your information when you visit dopove.com (the "Site"). Please read it carefully.
            </p>

            <div className="space-y-16 text-[#fefdfe]/80 leading-relaxed font-light">
              {/* Introduction */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">Introduction</h2>
                <p>
                  At Dopove Private Limited ("Dopove", "we", "us", or "our"), we are committed to protecting your privacy and ensuring the security of your data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (the "Site").
                </p>
                <p className="text-[#fefdfe]/70">
                  This Policy applies solely to the public website (dopove.com). Data processed through Dopove's enterprise software products (e.g., Indexless Context Engine (ICE), Cortex) is governed exclusively by separate Data Processing Agreements (DPA) and Master Service Agreements (MSA) executed with our enterprise clients.
                </p>
              </section>

              {/* 1. Lawful Basis (GDPR Art. 6) */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">1. Lawful Basis for Processing (GDPR Article 6)</h2>
                <p>
                  We process your personal data only where we have a valid lawful basis under applicable data protection laws (including GDPR Article 6):
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Legitimate Interest (Art. 6(1)(f)):</strong> Operating and improving the Site, security, fraud prevention, analytics, and responding to inquiries.</li>
                  <li><strong>Consent (Art. 6(1)(a)):</strong> Marketing communications (newsletter, product updates) where you have opted in.</li>
                  <li><strong>Contract Performance (Art. 6(1)(b)):</strong> Processing waitlist sign-ups and responding to your inquiries.</li>
                  <li><strong>Legal Obligation (Art. 6(1)(c)):</strong> Compliance with applicable laws, court orders, or regulatory requirements.</li>
                </ul>
                <p className="text-[#fefdfe]/70">
                  You may withdraw consent for marketing at any time by clicking "unsubscribe" in emails or contacting us at {COMPANY_EMAIL}.
                </p>
              </section>

              {/* 2. Information We Collect */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">2. Information We Collect</h2>
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-[#fefdfe]">A. Personal Information You Provide</h3>
                    <p>When you contact us, sign up for waitlists, or submit forms, we may collect:</p>
                    <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                      <li>Name and professional title</li>
                      <li>Email address and phone number</li>
                      <li>Company affiliation and industry</li>
                      <li>Nature of your inquiry or interest in our products</li>
                    </ul>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-[#fefdfe]">B. Automatically Collected Data</h3>
                    <p>We automatically collect certain information when you visit the Site:</p>
                    <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                      <li>IP address and geographical location (approximate)</li>
                      <li>Browser type, version, and language settings</li>
                      <li>Pages visited, time spent, and navigation paths</li>
                      <li>Referral sources and timestamps</li>
                      <li>Device type and operating system</li>
                    </ul>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-[#fefdfe]">C. Cookies and Tracking Technologies</h3>
                    <p>
                      We use cookies and similar technologies to operate the Site, analyze usage, and remember preferences. You can control cookies via your browser settings. For details, see our <a href="/cookie-policy" className="text-[#00d1ff] underline hover:text-[#6FB6E8]">Cookie Policy</a>.
                    </p>
                    <p>
                      Live chat: if you choose to allow it, Zoho SalesIQ sets cookies and processes your chat messages. We load it only after you click Allow chat, and you can change your choice at any time with Chat preferences in the footer.
                    </p>
                  </div>
                </div>
              </section>

              {/* 3. How We Use Your Data */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">3. How We Use Your Data</h2>
                <p>We use the information we collect for the following purposes:</p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li>Provide, maintain, and improve the Site and our services</li>
                  <li>Respond to your inquiries and provide support</li>
                  <li>Send technical notices, updates, and security alerts</li>
                  <li>Communicate about products and services (only with your consent / opt-in)</li>
                  <li>Analyze Site performance and user behavior to improve experience</li>
                  <li>Security, fraud prevention, and abuse detection</li>
                  <li>Comply with legal obligations and protect our rights</li>
                </ul>
                <p className="pt-4 italic text-[#e1dabe]">
                  Dopove does not sell, rent, or trade your personal data to third parties for marketing purposes.
                </p>
              </section>

              {/* 4. Enterprise Services Exclusion */}
              <section className="space-y-6 bg-[#23263a]/50 p-8 rounded-3xl border border-[#fefdfe]/10">
                <h2 className="text-3xl font-bold text-[#e1dabe] tracking-tight">4. Enterprise Services Exclusion</h2>
                <p>
                  This Privacy Policy applies solely to visitors of the public website. Data processed through Dopove's enterprise software products — specifically the <strong>Indexless Context Engine (ICE)</strong> and <strong>Cortex</strong> — is governed exclusively by separate, legally binding <strong>Data Processing Agreements (DPA)</strong> and <strong>Master Service Agreements (MSA)</strong> executed with our enterprise clients.
                </p>
                <div className="space-y-4 mt-6">
                  <h3 className="text-xl font-semibold text-[#fefdfe]">Anonymized Architecture</h3>
                  <p>
                    ICE is designed with a "Local-First" privacy architecture. All data ingestion and processing occur within the Client's own <strong>Virtual Private Cloud (VPC)</strong>. Dopove does not ingest, store, or process raw Personally Identifiable Information (PII) or proprietary source code from enterprise deployments.
                  </p>
                </div>
                <div className="space-y-4 mt-6">
                  <h3 className="text-xl font-semibold text-[#fefdfe]">Telemetry and Performance Metrics</h3>
                  <p>
                    Dopove processes exclusively anonymized telemetry and performance metrics to maintain and optimize our latency optimization engine. This data is scrubbed of all PII before transmission.
                  </p>
                </div>
              </section>

              {/* 5. Data Sharing & Disclosure */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">5. Data Sharing & Disclosure</h2>
                <p>We do not sell your personal data. We may share your information only as follows:</p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Service Providers:</strong> Trusted third parties who process data on our behalf (hosting, analytics, email) under strict Data Processing Agreements.</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or regulatory authority.</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice).</li>
                  <li><strong>Legal Defense:</strong> To protect our rights, property, or safety, or those of others.</li>
                </ul>
                <p className="pt-4 italic text-[#e1dabe]">
                  We do not sell, rent, or trade your personal data to third parties for marketing purposes.
                </p>
              </section>

              {/* 6. International Data Transfers */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">6. International Data Transfers</h2>
                <p>
                  Dopove is headquartered in India. Your information may be transferred to and processed in countries outside your jurisdiction (including India, the United States, and the European Union). We ensure such transfers comply with applicable data protection laws through:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Adequacy Decisions:</strong> Transfers to countries with EU adequacy decisions.</li>
                  <li><strong>Standard Contractual Clauses (SCCs):</strong> EU Commission-approved clauses for transfers to third countries.</li>
                  <li><strong>Binding Corporate Rules (BCRs):</strong> Where applicable for intra-group transfers.</li>
                  <li><strong>Derogations:</strong> Your explicit consent or contract necessity (Art. 49 GDPR).</li>
                </ul>
                <p className="text-[#fefdfe]/70">
                  A copy of the safeguards used for international transfers is available upon request to {DPO_EMAIL}.
                </p>
                <p className="text-[#fefdfe]/70">
                  For personal data covered by the DPDP Act, transfers outside India are permitted except to countries the Central Government restricts by notification under Section 16 of the DPDP Act. We will comply with any such notification.
                </p>
              </section>

              {/* 7. Data Security */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">7. Data Security</h2>
                <p>
                  We implement industry-standard technical and organizational measures to secure your information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Encryption:</strong> TLS 1.2+ for data in transit; AES-256 for data at rest.</li>
                  <li><strong>Access Controls:</strong> Role-based access, MFA, least privilege, and regular audits.</li>
                  <li><strong>Monitoring:</strong> Intrusion detection, vulnerability scanning, and incident response.</li>
                  <li><strong>Retention:</strong> Data retained only as long as necessary (see Retention below).</li>
                </ul>
                <p className="pt-4 text-[#fefdfe]/70">
                  While we implement strong safeguards, no method of internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security.
                </p>
              </section>

              {/* 7. Data Retention */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">8. Data Retention</h2>
                <p>We retain personal data only as long as necessary for the purposes outlined in this Policy:</p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Waitlist / Contact Form Data:</strong> 2 years from last interaction, or until you request deletion.</li>
                  <li><strong>Support Tickets / Correspondence:</strong> 3 years after resolution.</li>
                  <li><strong>Analytics / Usage Data:</strong> Aggregated/anonymized after 13 months; raw logs deleted after 30 days.</li>
                  <li><strong>Marketing Data:</strong> Until you unsubscribe or 2 years of inactivity.</li>
                  <li><strong>Legal / Compliance:</strong> As required by applicable law (up to 7 years).</li>
                </ul>
                <p className="text-[#fefdfe]/70">
                  You may request deletion at any time (see Your Rights). Anonymized/aggregated data may be retained indefinitely.
                </p>
              </section>

              {/* 8. Your Rights (GDPR Art. 15-22, CCPA/CPRA) */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">9. Your Rights</h2>
                <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Access (Art. 15 GDPR):</strong> Receive a copy of your data and processing details.</li>
                  <li><strong>Rectification (Art. 16 GDPR):</strong> Correct inaccurate or incomplete data.</li>
                  <li><strong>Erasure / "Right to be Forgotten" (Art. 17 GDPR):</strong> Request deletion of your data.</li>
                  <li><strong>Restriction (Art. 18 GDPR):</strong> Limit how we process your data.</li>
                  <li><strong>Objection (Art. 21 GDPR):</strong> Object to processing based on legitimate interest or direct marketing.</li>
                  <li><strong>Portability (Art. 20 GDPR):</strong> Receive your data in a structured, machine-readable format.</li>
                  <li><strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw at any time.</li>
                  <li><strong>CCPA/CPRA Rights (California):</strong> Know, delete, opt-out of sale/share, non-discrimination, limit sensitive data use.</li>
                </ul>
                <p className="pt-4">
                  To exercise any right, contact our Data Protection Officer at <a href={`mailto:${DPO_EMAIL}`} className="text-[#00d1ff] underline hover:text-[#6FB6E8]">{DPO_EMAIL}</a> with "Privacy Rights Request" in the subject. We will confirm we have received your request within 10 business days and respond within 45 days of receiving it (or sooner where the law requires, for example 30 days under the GDPR). If a request is complex or we receive many, we may extend that period by up to 60 days, and we will tell you why before the first period ends.
                </p>
                <p className="text-[#fefdfe]/70 text-sm">
                  We may verify your identity before fulfilling requests. No fee is charged unless requests are manifestly unfounded or excessive. If we decline all or part of a request, we will tell you the reason.
                </p>
                <p className="text-[#fefdfe]/70 text-sm">
                  You also have the right to lodge a complaint with a data protection supervisory authority, in the country where you live or work or where you believe your rights were infringed. In the UK this is the Information Commissioner's Office. You do not have to contact us first, although we would like the chance to put things right.
                </p>
              </section>

              {/* 8. Automated Decision-Making */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">10. Automated Decision-Making & Profiling</h2>
                <p>
                  We do not engage in fully automated decision-making (including profiling) that produces legal effects or similarly significantly affects you, as defined by GDPR Article 22. Analytics and security scoring are used only for operational purposes and are subject to human review.
                </p>
              </section>

              {/* 9. Children's Privacy (COPPA) */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">11. Children's Privacy</h2>
                <p>
                  The Site is not directed to children. Under the Digital Personal Data Protection Act, 2023 (DPDP Act) a child is anyone under 18. We do not knowingly process personal data of children under 18, and processing a child's personal data requires the verifiable consent of a parent or lawful guardian (Section 9 of the DPDP Act). If we learn we have collected data from a child without that consent, we will delete it promptly. If you believe we have collected information from a child, contact us at {COMPANY_EMAIL}.
                </p>
              </section>

              {/* 10. California Privacy Rights (CCPA/CPRA) */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">12. California Privacy Rights (CCPA/CPRA)</h2>
                <p>
                  If you are a California resident, you have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li>Know what personal information is collected, used, shared, or sold.</li>
                  <li>Delete your personal information (with exceptions).</li>
                  <li>Correct inaccurate personal information.</li>
                  <li>Opt-out of the sale or sharing of your personal information.</li>
                  <li>Non-discrimination for exercising your rights.</li>
                  <li>Limit the use and disclosure of sensitive personal information.</li>
                </ul>
                <p className="pt-4">
                  Dopove does not sell or share personal information as defined by the CCPA. To submit a request, email {COMPANY_EMAIL} with "CCPA Request" in the subject.
                </p>
              </section>

              {/* 13. India DPDP Act */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">13. Digital Personal Data Protection Act, 2023 (India)</h2>
                <p>Dopove Private Limited is incorporated in India. For personal data of individuals in India, we process it in line with the Digital Personal Data Protection Act, 2023: we ask for consent where it is the basis for processing, we collect only what each purpose needs, and we keep it only as long as that purpose requires.</p>
                <p>Under that Act you may ask us for a summary of the personal data we process about you, and to correct, complete, update or erase it, and you may nominate another person to exercise these rights if you die or become unable to. You may also use our grievance contact below; if we do not resolve your complaint, you may complain to the Data Protection Board of India. We acknowledge grievances within a reasonable time and seek to resolve them promptly, in accordance with the DPDP Act and applicable Rules.</p>
                <p>Dopove Private Limited has not been notified as a Significant Data Fiduciary under Section 10 of the DPDP Act.</p>
              </section>

              {/* Subprocessors */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">Subprocessors</h2>
                <p>We use the following subprocessors to operate this website. Each processes personal data only on our instructions and under a data processing agreement or equivalent terms.</p>
                <ul className="list-disc pl-6 space-y-2 text-[#fefdfe]/70">
                  <li><strong>Amazon Web Services (United States, us-east-1):</strong> user sign-in (Amazon Cognito), the waitlist and account API (API Gateway, Lambda, DynamoDB) and transactional email (Amazon SES).</li>
                  <li><strong>Formspree:</strong> receives contact form submissions (name, email address and message) and relays them to us.</li>
                  <li><strong>Google Cloud:</strong> hosts this website.</li>
                  <li><strong>Zoho (India data centre):</strong> live chat (Zoho SalesIQ), if you allow it, and our CRM, where contact form and waitlist enquiries are stored as leads.</li>
                </ul>
                <p className="text-[#fefdfe]/70">We will update this list when we add or change a subprocessor. To ask about a subprocessor, contact us at {COMPANY_EMAIL}.</p>
              </section>

              {/* Personal Data Breach Notification */}
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">Personal Data Breach Notification</h2>
                <p>If we become aware of a personal data breach affecting data that we control, we will notify the Data Protection Board of India and each affected Data Principal as required by Section 8(6) of the DPDP Act and its Rules. Where the GDPR applies, we will also notify the competent supervisory authority without undue delay and the breach is likely to result in a risk to the rights and freedoms of individuals, within 72 hours of becoming aware of it.</p>
                <p>Where a breach is likely to result in a high risk to you, we will also notify you directly without undue delay, describing what happened, the likely consequences and the steps we are taking.</p>
              </section>

              {/* 13. Contact Us */}
              <section className="space-y-6 border-t border-[#fefdfe]/10 pt-12">
                <h2 className="text-3xl font-bold text-[#fefdfe] tracking-tight">Contact Us / Data Protection Officer</h2>
                <p>
                  If you have any questions about this Privacy Policy, our data practices, or wish to exercise your rights, contact our Data Protection Officer at:
                </p>
                <div className="text-[#e1dabe] font-mono">
                  Email: <a href={`mailto:${DPO_EMAIL}`} className="text-[#00d1ff] underline hover:text-[#6FB6E8]">{DPO_EMAIL}</a><br />
                  Subject: Privacy Inquiry / Data Rights Request<br />
                  {COMPANY_ADDRESS}
                </div>
                <p className="text-[#fefdfe]/70 text-sm pt-4">
                  We will respond to all legitimate requests within 30 days (or as required by applicable law).
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default PrivacyPolicy;